import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Laptop, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { THEMES, applyTheme, readTheme, type Theme } from "../../theme/theme.js";

const ICON: Record<Theme, typeof Sun> = { system: Laptop, light: Sun, dark: Moon };

/**
 * Tri-state appearance control: **System / Light / Dark**.
 *
 * Tri-state, not a switch: a binary toggle cannot express "follow the OS", which is both the correct default and
 * the only state that keeps tracking when the user changes their OS setting while the app is open. `system` is
 * represented by the ABSENCE of `data-theme`, so `prefers-color-scheme` stays in charge.
 *
 * Replaces a native `<select>` that shipped as a stopgap. It was operable, but rendered as a raw browser control
 * with its label crammed against it, and it could not show which mode is active in the trigger — the one thing
 * this control should communicate at a glance.
 *
 * A `role="menu"` is correct HERE because every item is interactive — unlike `AccountPanel`, where static identity
 * text inside a menu would be skipped by screen readers in application mode.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const Icon = ICON[theme];
  // `appearance.system | light | dark` — built from the key rather than a lookup table, so adding a THEMES entry
  // without its translation is a missing-key failure instead of `undefined` rendered into the menu.
  const label = (mode: Theme) => t(`appearance.${mode}`);

  return (
    <DropdownMenuTrigger>
      {/* The trigger icon reflects the ACTIVE mode, so current state is visible without opening anything. */}
      <Button variant="ghost" size="icon" aria-label={t("appearance.current", { mode: label(theme) })}>
        <Icon aria-hidden="true" className="size-4" />
      </Button>

      <DropdownMenu
        selectionMode="single"
        selectedKeys={[theme]}
        onSelectionChange={(keys) => {
          const next = [...keys][0] as Theme | undefined;
          // Single-select can emit an empty set when the active item is re-pressed. Keep the current theme rather
          // than clearing it, which would drop the user to an undefined state.
          if (!next) return;
          setTheme(next);
          applyTheme(next);
        }}
      >
        <DropdownMenuLabel>{t("appearance.title")}</DropdownMenuLabel>
        {/* `themeKey`, not `t` — `t` is the translator now. */}
        {THEMES.map((themeKey) => {
          const ItemIcon = ICON[themeKey];
          return (
            <DropdownMenuItem key={themeKey} id={themeKey} textValue={label(themeKey)}>
              <ItemIcon aria-hidden="true" className="size-4" />
              {label(themeKey)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenu>
    </DropdownMenuTrigger>
  );
}
